import axios from 'axios';
import * as Sentry from '@sentry/react';

// Global token getter function - will be set by AuthContext
let getAccessToken: (() => Promise<string | null>) | null = null;

export const setTokenGetter = (getter: () => Promise<string | null>) => {
  getAccessToken = getter;
};

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://localhost:7193/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000, // 30 second timeout to catch connection issues early
});

// Request interceptor to add Azure AD access token from MSAL
api.interceptors.request.use(
  async (config) => {
    // Get token from MSAL via TokenService
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        
        // Validate token type and value
        if (token && typeof token === 'string' && token.length > 0) {
          // Validate JWT format before using
          const parts = token.split('.');
          if (parts.length !== 3) {
            return config;
          }
          
          config.headers.Authorization = `Bearer ${token}`;
          
        } else {
          console.warn('⚠️ Invalid or missing token for request:', {
            url: config.url,
            tokenType: typeof token,
            tokenValue: token,
            isNull: token === null,
            isUndefined: token === undefined
          });
        }
      } catch (error) {
        console.error('❌ Error getting access token:', error);
        Sentry.captureException(error, {
          tags: { component: 'api', action: 'get_token' },
          extra: { url: config.url },
        });
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Capture network errors (connection timeout, network unreachable, etc.)
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const errorContext = {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        errorCode: error.code,
        errorMessage: error.message,
        timeout: error.config?.timeout,
      };
      
      console.error('❌ Network connectivity error - API unreachable', errorContext);
      
      Sentry.captureException(error, {
        tags: { 
          error_type: 'network_error',
          error_code: error.code,
          endpoint: error.config?.url,
        },
        extra: errorContext,
        fingerprint: ['network-error', error.config?.url || 'unknown'],
      });
    }
    
    if (error.response?.status === 401) {
      const authHeader = error.config?.headers?.Authorization;
      if (authHeader) {
        console.error('🔑 Token info:', {
          hasToken: !!authHeader,
          tokenType: authHeader.split(' ')[0],
          tokenLength: authHeader.split(' ')[1]?.length || 0
        });
      } else {
        console.error('⚠️ No Authorization header present in failed request');
      }
      
      console.warn('⚠️ Authentication failed - 401 Unauthorized', {
        endpoint: error.config?.url,
        hasToken: !!authHeader,
      });
      
      // Prevent infinite loop: redirect to /login, not /
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // Capture server errors (5xx)
    if (error.response?.status >= 500) {
      Sentry.captureException(error, {
        tags: { 
          error_type: 'server_error',
          status_code: error.response.status,
          endpoint: error.config?.url,
        },
        extra: {
          responseData: error.response?.data,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
        },
      });
    }
    
    return Promise.reject(error);
  }
);

export default api;
